import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/api/client';

const AuthContext = createContext({ user: null, loading: true, refresh: () => {}, logout: async () => {} });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // useCallback stabilizes the reference so memoized consumers don't re-render unnecessarily.
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Best-effort logout: backend session is non-critical to clear from client.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Server-side logout failed (clearing local state anyway):', e?.message || e);
      }
    }
    setUser(null);
  }, []);

  // useMemo prevents creating a fresh object on every render, which would
  // otherwise force every consumer of useAuth() to re-render.
  const value = useMemo(
    () => ({ user, loading, refresh: checkAuth, setUser, logout }),
    [user, loading, checkAuth, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
